import {
  IonButton,
  IonContent,
  IonInput,
  IonLoading,
  IonModal,
  IonSelect,
  IonSelectOption
} from '@ionic/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  ButtonContainer,
  ModalHeader,
  Padding,
  Spacer
} from '@/components/common';
import { Icon, Text } from '@/components/memo';
import { useClinic, useDentist } from '@/contexts';
import { useKeyboardAwareInput, useToast } from '@/hooks';
import {
  ACCESS_TYPES,
  ACCESS_TYPE_LABELS,
  MOBILE_NUMBER_LENGTH,
  WEEKLY_REPORT_SUBSCRIPTION_TYPES
} from '@/utils/constants';
import { formatDentistName } from '@/utils/helpers/formatDentistName';
import { getPossessiveName } from '@/utils/helpers/formatName';
import { handleIonMobileNumberInput } from '@/utils/helpers/sanitizeMobileNumberInput';
import { Access, WeeklyReportSubscriptionType } from '@/utils/types';
import {
  arrowBackOutline,
  bulbOutline,
  checkmarkCircleOutline
} from 'ionicons/icons';

import './UpdateAccess.css';

interface UpdateAccessProps {
  isOpen: boolean;
  onClose: () => void;
  presentingElement?: HTMLElement | undefined;
}

const UpdateAccess: React.FC<UpdateAccessProps> = ({
  isOpen,
  onClose,
  presentingElement
}) => {
  const displayToast = useToast();
  const {
    selectedClinic: clinic,
    fetchClinicByMobileNumber,
    clinicByMobileNumber,
    loadingClinicByMobileNumber
  } = useClinic();
  const { selectedDentist: dentist, updateDentistMutation } = useDentist();

  const [mobileNumber, setMobileNumber] = useState<string | null>(null);
  const [access, setAccess] = useState<Access>(ACCESS_TYPES.EDIT);
  const [weeklyReportSubscription, setWeeklyReportSubscription] =
    useState<WeeklyReportSubscriptionType>(
      WEEKLY_REPORT_SUBSCRIPTION_TYPES.NONE
    );
  const [hasError, setHasError] = useState<boolean>(false);

  const containerRef = useRef<HTMLIonContentElement | null>(null);
  const mobileNumberInputRef = useRef<HTMLIonInputElement | null>(null);
  /**
   * Manages keyboard-aware scrolling behavior for the reason input field in the modal.
   * This custom implementation addresses the limitations of the default hook behavior
   * in this specific context where the modal's bottom space is constrained.
   *
   * @see useKeyboardAwareInput - The base hook that provides keyboard awareness functionality
   * @note This implementation ensures the input field remains visible when the keyboard appears,
   *       preventing it from being obscured by the keyboard in the modal's constrained space.
   */
  useKeyboardAwareInput({
    inputRef: mobileNumberInputRef,
    containerRef
  });

  useEffect(() => {
    if (dentist) {
      setMobileNumber(dentist.mobile_number || null);
      setAccess(dentist.access || ACCESS_TYPES.EDIT);
      setWeeklyReportSubscription(
        dentist.weekly_report_subscription ||
          WEEKLY_REPORT_SUBSCRIPTION_TYPES.NONE
      );
    }
  }, [
    dentist?.mobile_number,
    dentist?.access,
    dentist?.weekly_report_subscription
  ]);

  const formattedDentistName = formatDentistName({
    dentist
  });

  const copy = {
    subHeader: 'ACCESS',
    header: (
      <>
        Manage {getPossessiveName(formattedDentistName)}{' '}
        <Text color="primary">access</Text> to their schedule
      </>
    ),
    message: `Update ${getPossessiveName(formattedDentistName)} access level and mobile number.`,
    buttonText: 'Update',
    inputLabel: 'Mobile Number',
    inputPlaceholder: '9---------',
    countryCode: '+63',
    selectLabel: 'Access Level',
    selectPlaceholder: 'Select access level',
    weeklyReportSubscriptionLabel: 'Weekly Reports',
    weeklyReportSubscriptionPlaceholder: 'Select weekly report subscription'
  };

  const onNext = useCallback(async () => {
    if (
      mobileNumber &&
      (mobileNumber.length !== MOBILE_NUMBER_LENGTH ||
        !/^\d+$/.test(mobileNumber))
    ) {
      setHasError(true);
      displayToast({
        message: 'Please enter a valid mobile number.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
      return;
    }

    try {
      setHasError(false);

      if (mobileNumber) {
        fetchClinicByMobileNumber(mobileNumber);
        if (clinicByMobileNumber || mobileNumber === clinic?.mobile_number) {
          displayToast({
            message:
              'This mobile number is already registered. Please use a different number or leave it blank.',
            duration: 3000,
            position: 'bottom',
            positionAnchor: 'next-button'
          });
          return;
        }
      }

      if (!dentist) {
        throw new Error();
      }

      await updateDentistMutation.mutateAsync({
        dentistId: dentist.id!,
        dentist: {
          first_name: dentist.first_name,
          last_name: dentist.last_name,
          clinic_id: dentist.clinic_id,
          mobile_number: mobileNumber ? mobileNumber : null,
          access: access,
          weekly_report_subscription: weeklyReportSubscription
        }
      });
      displayToast({
        message: 'Dentist access updated successfully.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
      onClose();
    } catch {
      displayToast({
        message: 'Something went wrong. Please try again.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    }
  }, [
    mobileNumber,
    access,
    weeklyReportSubscription,
    clinic,
    dentist,
    clinicByMobileNumber,
    fetchClinicByMobileNumber,
    updateDentistMutation,
    onClose
  ]);

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      presentingElement={presentingElement}
    >
      <IonContent ref={containerRef}>
        <Padding withSafeArea={!presentingElement}>
          <div className="update-access">
            <ModalHeader
              leftIcon={arrowBackOutline}
              onClickLeftIcon={onClose}
            />
            <Spacer top={16} bottom={24}>
              <h1 className="update-access__header">{copy.header}</h1>
              <p className="update-access__message">{copy.message}</p>
            </Spacer>

            <Spacer bottom={124}>
              <div className="update-access__form">
                <div className="update-access__input">
                  <IonSelect
                    label={copy.selectLabel}
                    labelPlacement="stacked"
                    placeholder={copy.selectPlaceholder}
                    value={access}
                    onIonChange={(e: CustomEvent) => setAccess(e.detail.value)}
                  >
                    <IonSelectOption value={ACCESS_TYPES.NONE}>
                      {ACCESS_TYPE_LABELS.NONE}
                    </IonSelectOption>
                    <IonSelectOption value={ACCESS_TYPES.VIEW}>
                      {ACCESS_TYPE_LABELS.VIEW}
                    </IonSelectOption>
                    <IonSelectOption value={ACCESS_TYPES.EDIT}>
                      {ACCESS_TYPE_LABELS.EDIT}
                    </IonSelectOption>
                  </IonSelect>
                </div>
                <div className="update-access__tip">
                  <Icon icon={bulbOutline} />
                  <Text color="warning">
                    <strong>Tip:</strong> Dentists can log in to Odonto with
                    their mobile number—no signup required.
                  </Text>
                </div>
                <div
                  className={`update-access__input ${hasError ? 'update-access__input--error' : ''}`}
                >
                  <IonInput
                    ref={mobileNumberInputRef}
                    id="mobile-number-input"
                    label={copy.inputLabel}
                    labelPlacement="stacked"
                    value={mobileNumber}
                    placeholder={copy.inputPlaceholder}
                    onIonInput={(e: CustomEvent) =>
                      handleIonMobileNumberInput(e, setMobileNumber)
                    }
                    maxlength={10}
                  >
                    <div slot="start">{copy.countryCode}</div>
                  </IonInput>
                </div>
                <div className="update-access__input">
                  <IonSelect
                    label={copy.weeklyReportSubscriptionLabel}
                    labelPlacement="stacked"
                    placeholder={copy.weeklyReportSubscriptionPlaceholder}
                    value={weeklyReportSubscription}
                    onIonChange={(e: CustomEvent) =>
                      setWeeklyReportSubscription(e.detail.value)
                    }
                  >
                    <IonSelectOption
                      value={WEEKLY_REPORT_SUBSCRIPTION_TYPES.CLINIC}
                    >
                      Will receive clinic reports (for clinic owners)
                    </IonSelectOption>
                    <IonSelectOption
                      value={WEEKLY_REPORT_SUBSCRIPTION_TYPES.PERSONAL}
                    >
                      Will receive personal reports (for dentists)
                    </IonSelectOption>
                    <IonSelectOption
                      value={WEEKLY_REPORT_SUBSCRIPTION_TYPES.NONE}
                    >
                      Will not receive any reports
                    </IonSelectOption>
                  </IonSelect>
                </div>
              </div>
            </Spacer>

            <ButtonContainer>
              <IonButton
                id="next-button"
                onClick={onNext}
                expand="block"
                fill="solid"
                size="default"
                color="primary"
                strong={true}
                disabled={
                  updateDentistMutation.isPending || loadingClinicByMobileNumber
                }
              >
                <Icon slot="start" icon={checkmarkCircleOutline} />
                {copy.buttonText}
              </IonButton>
            </ButtonContainer>
          </div>
          <IonLoading
            isOpen={
              updateDentistMutation.isPending || loadingClinicByMobileNumber
            }
            message="Loading..."
            spinner="crescent"
          />
        </Padding>
      </IonContent>
    </IonModal>
  );
};

export default UpdateAccess;
